/**
 * SCR-022 — Nhập / sửa điểm lớp (Teacher)
 * Route: /teacher/classes/:classId/grades
 * Thang điểm 100. Mobile: column selector → list HV dọc 1 ô/HV.
 * Desktop md+: full matrix HV × columns (horizontal scroll).
 * Inline save on blur/Enter. Optimistic indicator per cell.
 */
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  useClassGrades,
  useUpsertGrade,
  useDeleteGrade,
  useTeacherClassDetail,
} from '@client/lib/queries/use-teacher';
import type { GradeColumn, GradeEntry } from '@client/lib/queries/use-teacher';

export const Route = createFileRoute('/teacher/classes/$classId/grades')({
  component: GradesTab,
});

// ---------------------------------------------------------------------------
const GRADE_TYPE_META: Record<string, { label: string; variant: 'info' | 'warning' | 'destructive' | 'success' | 'secondary' }> = {
  quiz:          { label: 'Quiz',        variant: 'info' },
  midterm:       { label: 'Giữa kỳ',    variant: 'warning' },
  final:         { label: 'Cuối kỳ',    variant: 'destructive' },
  participation: { label: 'Tham gia',   variant: 'success' },
  assignment:    { label: 'Bài tập',    variant: 'secondary' },
};

const GRADE_TYPES = Object.entries(GRADE_TYPE_META).map(([value, meta]) => ({ value, ...meta }));

// ---------------------------------------------------------------------------
// A pending column is a client-side virtual column added before any grade is saved.
// Columns are derived server-side from existing grades — no grade-column table exists.
// When teacher saves the first grade for a pending column, the column materialises in the DB.
interface PendingColumn {
  key: string; // local unique key e.g. "pending:quiz:1"
  label: string;
  gradeType: string;
  maxScore: number;
  assignmentId: null;
}

function GradesTab() {
  const { classId } = Route.useParams();
  const { data: cls } = useTeacherClassDetail(classId);
  const { data, isLoading, isError, refetch } = useClassGrades(classId);
  const [selectedColumnKey, setSelectedColumnKey] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [newColOpen, setNewColOpen] = useState(false);
  // Pending columns exist only in local state until first grade is saved
  const [pendingColumns, setPendingColumns] = useState<PendingColumn[]>([]);

  const isReadOnly = cls?.status === 'cancelled' || cls?.status === 'finished';

  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>Không tải được bảng điểm.</span>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Thử lại
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!data) return null;

  const { enrollments, columns, grades } = data;

  // Merge server columns + pending local columns
  // Pending columns that match a server column key are dropped (server took over)
  const serverKeySet = new Set(columns.map((c) => c.key));
  const activePending = pendingColumns.filter((p) => !serverKeySet.has(p.key));
  const allColumns: GradeColumn[] = [
    ...columns,
    ...activePending.map((p) => ({ ...p, key: p.key })),
  ];

  const filteredColumns = typeFilter === 'all'
    ? allColumns
    : allColumns.filter((c) => c.gradeType === typeFilter);

  // Default selected column
  const activeColumnKey = selectedColumnKey || filteredColumns[0]?.key || '';
  const activeColumn = filteredColumns.find((c) => c.key === activeColumnKey);

  // Grade lookup: enrollmentId + columnKey → GradeEntry
  const gradeMap = new Map<string, GradeEntry>();
  for (const g of grades) {
    gradeMap.set(`${g.enrollmentId}:${g.columnKey}`, g);
  }

  // Compute average per student
  function avgScore(enrollmentId: string): string {
    const studentGrades = grades.filter(
      (g) => g.enrollmentId === enrollmentId && g.score != null,
    );
    if (studentGrades.length === 0) return '—';
    const sum = studentGrades.reduce((acc, g) => acc + (g.score ?? 0), 0);
    return (sum / studentGrades.length).toFixed(1);
  }

  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="Loại điểm" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả loại</SelectItem>
            {GRADE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!isReadOnly && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setNewColOpen(true)}
            className="min-h-[44px] w-full sm:w-auto"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Cột điểm mới
          </Button>
        )}
      </div>

      {enrollments.length === 0 && (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Lớp chưa có học viên.
        </p>
      )}

      {enrollments.length > 0 && filteredColumns.length === 0 && (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Chưa có cột điểm.{!isReadOnly && ' Bấm "+ Cột điểm mới" để thêm.'}
        </p>
      )}

      {/* Mobile: column selector → 1-col list */}
      {enrollments.length > 0 && filteredColumns.length > 0 && (
        <>
          {/* Mobile view — hidden md+ */}
          <div className="md:hidden space-y-3">
            {/* Column picker */}
            <Select value={activeColumnKey} onValueChange={setSelectedColumnKey}>
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Chọn cột điểm" />
              </SelectTrigger>
              <SelectContent>
                {filteredColumns.map((col) => (
                  <SelectItem key={col.key} value={col.key}>
                    {col.label}
                    {col.maxScore !== 100 && ` /${col.maxScore}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {activeColumn && (
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={GRADE_TYPE_META[activeColumn.gradeType]?.variant} className="text-xs">
                  {GRADE_TYPE_META[activeColumn.gradeType]?.label ?? activeColumn.gradeType}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Thang {activeColumn.maxScore} điểm
                </span>
              </div>
            )}

            {activeColumn && enrollments.map((enr) => {
              const entry = gradeMap.get(`${enr.id}:${activeColumnKey}`);
              return (
                <MobileGradeRow
                  key={enr.id}
                  classId={classId}
                  enrollmentId={enr.id}
                  studentCode={enr.student.studentCode}
                  fullName={enr.student.fullName}
                  column={activeColumn}
                  entry={entry}
                  readOnly={isReadOnly}
                />
              );
            })}
          </div>

          {/* Desktop matrix — hidden on mobile */}
          <div className="hidden md:block rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 text-left font-medium sticky left-0 bg-muted/40 min-w-[180px]">
                    Học viên
                  </th>
                  {filteredColumns.map((col) => (
                    <th key={col.key} className="px-3 py-3 text-center font-medium min-w-[100px]">
                      <div className="flex flex-col items-center gap-1">
                        <span>{col.label}</span>
                        <Badge variant={GRADE_TYPE_META[col.gradeType]?.variant} className="text-xs">
                          {GRADE_TYPE_META[col.gradeType]?.label ?? col.gradeType}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">/{col.maxScore}</span>
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center font-medium min-w-[80px]">TB</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((enr) => (
                  <tr key={enr.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2 sticky left-0 bg-card">
                      <p className="font-medium text-sm">{enr.student.fullName}</p>
                      <p className="font-mono text-xs text-muted-foreground">{enr.student.studentCode}</p>
                    </td>
                    {filteredColumns.map((col) => {
                      const entry = gradeMap.get(`${enr.id}:${col.key}`);
                      return (
                        <td key={col.key} className="px-3 py-2 text-center">
                          <GradeCell
                            classId={classId}
                            enrollmentId={enr.id}
                            column={col}
                            entry={entry}
                            readOnly={isReadOnly}
                          />
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center text-sm font-medium text-muted-foreground">
                      {avgScore(enr.id)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* New column dialog — creates client-side pending column; DB column materialises on first grade save */}
      {newColOpen && (
        <NewColumnDialog
          onClose={() => setNewColOpen(false)}
          onAdd={(col) => {
            setPendingColumns((prev) => [...prev, col]);
            setNewColOpen(false);
            toast.success('Đã thêm cột điểm. Nhập điểm để lưu.');
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grade cell (desktop inline edit)
// ---------------------------------------------------------------------------
function GradeCell({
  classId,
  enrollmentId,
  column,
  entry,
  readOnly,
}: {
  classId: string;
  enrollmentId: string;
  column: GradeColumn;
  entry: GradeEntry | undefined;
  readOnly: boolean;
}) {
  const upsert = useUpsertGrade(classId);
  const del = useDeleteGrade(classId);
  const [val, setVal] = useState(entry?.score != null ? String(entry.score) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(entry?.feedback ?? '');

  async function handleSave() {
    if (val === (entry?.score != null ? String(entry.score) : '')) return;
    if (val === '' && entry?.gradeId) {
      setSaving(true);
      try {
        await del.mutateAsync(entry.gradeId);
        setError('');
      } catch {
        setError('Lỗi xóa điểm');
      } finally {
        setSaving(false);
      }
      return;
    }
    const num = parseFloat(val);
    if (isNaN(num) || num < 0 || num > column.maxScore) {
      setError(`0–${column.maxScore}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await upsert.mutateAsync({
        gradeId: entry?.gradeId,
        enrollmentId,
        gradeType: column.gradeType,
        assignmentId: column.assignmentId ?? undefined,
        score: num,
        maxScore: column.maxScore,
        feedback: feedback || undefined,
      });
    } catch {
      setError('Lỗi lưu');
    } finally {
      setSaving(false);
    }
  }

  if (readOnly) {
    return (
      <span className="text-sm">
        {entry?.score != null ? entry.score : '—'}
      </span>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex flex-col items-center gap-0.5 cursor-pointer group">
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            max={column.maxScore}
            value={val}
            onChange={(e) => { setVal(e.target.value); setError(''); }}
            onBlur={() => void handleSave()}
            onKeyDown={(e) => e.key === 'Enter' && void handleSave()}
            className={`h-8 w-16 text-center text-sm ${error ? 'border-destructive' : ''} ${saving ? 'opacity-60' : ''}`}
            disabled={saving}
          />
          {error && <span className="text-[10px] text-destructive">{error}</span>}
          {saving && <span className="text-[10px] text-muted-foreground">...</span>}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 space-y-2">
        <p className="text-xs font-medium">Nhận xét</p>
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Nhận xét cho học viên..."
          className="text-xs h-20 resize-none"
          maxLength={500}
        />
        <Button
          size="sm"
          className="w-full"
          onClick={() => void handleSave()}
          disabled={saving}
        >
          Lưu nhận xét
        </Button>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Mobile grade row
// ---------------------------------------------------------------------------
function MobileGradeRow({
  classId,
  enrollmentId,
  studentCode,
  fullName,
  column,
  entry,
  readOnly,
}: {
  classId: string;
  enrollmentId: string;
  studentCode: string;
  fullName: string;
  column: GradeColumn;
  entry: GradeEntry | undefined;
  readOnly: boolean;
}) {
  const upsert = useUpsertGrade(classId);
  const del = useDeleteGrade(classId);
  const [val, setVal] = useState(entry?.score != null ? String(entry.score) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleSave() {
    const currentVal = inputRef.current?.value ?? val;
    if (currentVal === (entry?.score != null ? String(entry.score) : '')) return;
    if (currentVal === '' && entry?.gradeId) {
      setSaving(true);
      try {
        await del.mutateAsync(entry.gradeId);
        setError('');
        toast.success('Đã xóa điểm.');
      } catch {
        setError('Lỗi xóa');
      } finally { setSaving(false); }
      return;
    }
    const num = parseFloat(currentVal);
    if (isNaN(num) || num < 0 || num > column.maxScore) {
      setError(`Điểm phải 0–${column.maxScore}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await upsert.mutateAsync({
        gradeId: entry?.gradeId,
        enrollmentId,
        gradeType: column.gradeType,
        assignmentId: column.assignmentId ?? undefined,
        score: num,
        maxScore: column.maxScore,
      });
      toast.success('Đã lưu điểm.');
    } catch {
      setError('Lỗi lưu điểm');
    } finally { setSaving(false); }
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 gap-3 min-h-[56px]">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{fullName}</p>
        <p className="text-xs font-mono text-muted-foreground">{studentCode}</p>
      </div>
      {readOnly ? (
        <span className="text-sm font-medium w-16 text-right">
          {entry?.score != null ? entry.score : '—'}
        </span>
      ) : (
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <Input
            ref={inputRef}
            type="number"
            inputMode="decimal"
            min={0}
            max={column.maxScore}
            value={val}
            onChange={(e) => { setVal(e.target.value); setError(''); }}
            onBlur={() => void handleSave()}
            onKeyDown={(e) => e.key === 'Enter' && void handleSave()}
            className={`h-9 w-20 text-center text-sm ${error ? 'border-destructive' : ''}`}
            disabled={saving}
            placeholder="—"
          />
          {error && <span className="text-[10px] text-destructive leading-tight">{error}</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New column dialog — client-side only; no API call.
// Creates a pending GradeColumn that materialises in DB when first grade is saved.
// ---------------------------------------------------------------------------
function NewColumnDialog({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (col: PendingColumn) => void;
}) {
  const [gradeType, setGradeType] = useState('quiz');
  const [label, setLabel] = useState('');
  const [maxScore, setMaxScore] = useState('100');
  const [error, setError] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) { setError('Nhập nhãn cột điểm.'); return; }
    const ms = parseFloat(maxScore);
    if (isNaN(ms) || ms <= 0 || ms > 100) { setError('Thang điểm 1–100.'); return; }
    setError('');
    onAdd({
      key: `pending:${gradeType}:${Date.now()}`,
      label: label.trim(),
      gradeType,
      maxScore: ms,
      assignmentId: null,
    });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-full max-w-sm">
        <DialogHeader>
          <DialogTitle>Thêm cột điểm mới</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="gt">Loại điểm</Label>
            <Select value={gradeType} onValueChange={setGradeType}>
              <SelectTrigger id="gt" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRADE_TYPES.filter((t) => t.value !== 'assignment').map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="col-label">Nhãn cột</Label>
            <Input
              id="col-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="vd: Quiz 1, Giữa kỳ 1..."
              maxLength={50}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max-score">Thang điểm tối đa</Label>
            <Input
              id="max-score"
              type="number"
              inputMode="decimal"
              value={maxScore}
              onChange={(e) => setMaxScore(e.target.value)}
              min={1}
              max={100}
              className="h-9 w-28"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Hủy
            </Button>
            <Button type="submit" className="flex-1">
              Thêm cột
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
